import React, { useState, useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Search, Loader2, Network, AlertCircle } from 'lucide-react'
import { exploreConnections, discoverMore, searchResources } from '../../services/api'
import type { ConnectionsNode, ConnectionsEdge, ResourceType, SearchResult } from '../../types'
import ConnectionsGraph from '../../components/graph/ConnectionsGraph'
import type { LayoutType } from '../../components/graph/ConnectionsGraph'
import ConnectionsControls from '../../components/graph/ConnectionsControls'
import ConnectionsLegend from '../../components/graph/ConnectionsLegend'
import NodeDetailPanel from '../../components/graph/NodeDetailPanel'
import DiscoveryProgress from '../../components/graph/DiscoveryProgress'

export default function Connections() {
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchSubmitted, setSearchSubmitted] = useState(false)
  const [startingResourceId, setStartingResourceId] = useState<string | null>(null)
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [showSearchResults, setShowSearchResults] = useState(false)

  // Graph state
  const [graphNodes, setGraphNodes] = useState<ConnectionsNode[]>([])
  const [graphEdges, setGraphEdges] = useState<ConnectionsEdge[]>([])
  const [selectedNode, setSelectedNode] = useState<ConnectionsNode | null>(null)
  const [layout, setLayout] = useState<LayoutType>('cose')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [discoveredCount, setDiscoveredCount] = useState(0)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

  const containerRef = useRef<HTMLDivElement>(null)
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Search for starting resource
  const searchMutation = useMutation({
    mutationFn: (query: string) =>
      searchResources({
        query,
        embeddings_mode: true,
        page: 1,
        page_size: 10,
      }),
    onSuccess: (data) => {
      setSearchResults(data.results)
      setShowSearchResults(true)
    },
  })

  // Explore connections for a resource
  const exploreMutation = useMutation({
    mutationFn: (resourceId: string) => exploreConnections(resourceId),
    onSuccess: (data) => {
      const nodes: ConnectionsNode[] = (data.nodes || []).map((n: any) => ({
        id: n.id || n.record_id || n.resource_id,
        label: n.label || n.title || 'Untitled',
        type: (n.type || n.resource_type || 'document') as ResourceType,
        score: n.score || n._score || 0,
      }))
      const edges: ConnectionsEdge[] = (data.edges || []).map((e: any) => ({
        source: e.source,
        target: e.target,
        type: e.type || 'similarity',
        weight: e.weight || 0.5,
        ai_discovered: e.ai_discovered || false,
      }))
      setGraphNodes(nodes)
      setGraphEdges(edges)
      setShowSearchResults(false)
      setExpandedNodes(new Set())
    },
  })

  // Discover more related resources
  const discoverMutation = useMutation({
    mutationFn: ({ nodeId, resourceTypes }: { nodeId: string; resourceTypes?: ResourceType[] }) =>
      discoverMore(nodeId, resourceTypes),
    onSuccess: (data) => {
      const newNodes: ConnectionsNode[] = (data.nodes || []).map((n: any) => ({
        id: n.id || n.record_id || n.resource_id,
        label: n.label || n.title || 'Untitled',
        type: (n.type || n.resource_type || 'document') as ResourceType,
        score: n.score || n._score || 0,
      }))
      const newEdges: ConnectionsEdge[] = (data.edges || []).map((e: any) => ({
        source: e.source,
        target: e.target,
        type: e.type || 'similarity',
        weight: e.weight || 0.5,
        ai_discovered: e.ai_discovered || true,
      }))

      // Merge with existing, deduplicating by id
      setGraphNodes(prev => {
        const existingIds = new Set(prev.map(n => n.id))
        const unique = newNodes.filter(n => !existingIds.has(n.id))
        setDiscoveredCount(unique.length)
        return [...prev, ...unique]
      })
      setGraphEdges(prev => {
        const existingKeys = new Set(prev.map(e => `${e.source}-${e.target}-${e.type}`))
        const unique = newEdges.filter(e => !existingKeys.has(`${e.source}-${e.target}-${e.type}`))
        return [...prev, ...unique]
      })
    },
  })

  // Handle search submission
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    if (!searchQuery.trim()) return
    setSearchSubmitted(true)
    searchMutation.mutate(searchQuery.trim())
  }, [searchQuery, searchMutation])

  // Handle selecting a search result to explore
  const handleSelectResource = useCallback((result: SearchResult) => {
    const resourceId = 'record_id' in result ? result.record_id : result.resource_id
    setStartingResourceId(resourceId)
    setSearchQuery(result.title)
    setShowSearchResults(false)
    exploreMutation.mutate(resourceId)
  }, [exploreMutation])

  // Handle node click in graph
  const handleNodeClick = useCallback((node: ConnectionsNode) => {
    setSelectedNode(node)
  }, [])

  // Handle discover more
  const handleDiscover = useCallback((resourceTypes?: ResourceType[]) => {
    const nodeId = selectedNode?.id || startingResourceId
    if (!nodeId) return
    setDiscoveredCount(0)
    discoverMutation.mutate({ nodeId, resourceTypes })
  }, [selectedNode, startingResourceId, discoverMutation])

  // Handle discover from detail panel
  const handleDiscoverFromNode = useCallback((node: ConnectionsNode) => {
    setDiscoveredCount(0)
    discoverMutation.mutate({ nodeId: node.id })
  }, [discoverMutation])

  // Handle cancel discovery
  const handleCancelDiscovery = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }, [])

  // Handle expand node (click-to-discover, Lambda returns 10 results by default)
  const EXPANDABLE_TYPES = ['journal', 'video', 'audio', 'image', 'website', 'course', 'document']

  const handleExpandNode = useCallback((nodeId: string, nodeType: string) => {
    // Skip if already expanded or not an expandable type
    if (expandedNodes.has(nodeId) || !EXPANDABLE_TYPES.includes(nodeType)) return
    // Skip if already discovering
    if (discoverMutation.isPending) return

    setExpandedNodes(prev => new Set(prev).add(nodeId))
    setDiscoveredCount(0)
    discoverMutation.mutate({ nodeId, resourceTypes: undefined })
  }, [expandedNodes, discoverMutation])

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    // The graph handles its own zoom via cytoscape ref;
    // this dispatches a custom event the graph component can listen for
    window.dispatchEvent(new CustomEvent('acad-graph-zoom', { detail: 'in' }))
  }, [])

  const handleZoomOut = useCallback(() => {
    window.dispatchEvent(new CustomEvent('acad-graph-zoom', { detail: 'out' }))
  }, [])

  // Fullscreen
  const handleFullscreen = useCallback(() => {
    if (!graphContainerRef.current) return
    if (!isFullscreen) {
      graphContainerRef.current.requestFullscreen?.()
    } else {
      document.exitFullscreen?.()
    }
    setIsFullscreen(!isFullscreen)
  }, [isFullscreen])

  // Handle open resource
  const handleOpenResource = useCallback((node: ConnectionsNode) => {
    window.open(`/resource/${node.id}`, '_blank')
  }, [])

  // Handle find related (re-centre graph on this node)
  const handleFindRelated = useCallback((node: ConnectionsNode) => {
    exploreMutation.mutate(node.id)
    setSelectedNode(null)
    setStartingResourceId(node.id)
  }, [exploreMutation])

  const hasGraph = graphNodes.length > 0

  return (
    <div className="flex flex-col h-full" ref={containerRef}>
      {/* Header with search */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3 mb-3">
          <Network className="w-6 h-6 text-acad-500" />
          <h1 className="text-xl font-bold text-gray-900">Connections Graph</h1>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="relative max-w-2xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                if (!e.target.value.trim()) setShowSearchResults(false)
              }}
              placeholder="Search for a resource to explore its connections..."
              className="w-full pl-10 pr-24 py-2.5 border border-gray-200 rounded-xl text-sm
                         focus:outline-none focus:ring-2 focus:ring-acad-500 focus:border-acad-500
                         placeholder-gray-400"
            />
            <button
              type="submit"
              disabled={searchMutation.isPending || !searchQuery.trim()}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 text-xs
                         font-semibold text-white bg-acad-500 rounded-lg hover:bg-acad-600
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searchMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                'Explore'
              )}
            </button>
          </div>

          {/* Search results dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200
                            rounded-xl shadow-lg z-30 max-h-80 overflow-y-auto">
              <div className="p-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">
                  Select a resource to explore
                </p>
                {searchResults.map((result) => {
                  const resourceType = ('resource_type' in result
                    ? result.resource_type
                    : 'journal') as ResourceType
                  const id = 'record_id' in result ? result.record_id : result.resource_id
                  return (
                    <button
                      key={id}
                      onClick={() => handleSelectResource(result)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-acad-50
                                 transition-colors flex items-start gap-2"
                    >
                      <div
                        className="w-2 h-2 rounded-sm shrink-0 mt-1.5"
                        style={{ backgroundColor: getTypeColor(resourceType) }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800 font-medium truncate">
                          {result.title}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {resourceType}
                          {result._score > 0 && ` -- Score: ${(result._score * 100).toFixed(0)}%`}
                        </p>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* No results */}
          {showSearchResults && searchResults.length === 0 && searchSubmitted && !searchMutation.isPending && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200
                            rounded-xl shadow-lg z-30 p-4 text-center">
              <AlertCircle className="w-5 h-5 text-gray-300 mx-auto mb-1" />
              <p className="text-sm text-gray-500">No resources found. Try a different query.</p>
            </div>
          )}
        </form>
      </div>

      {/* Graph area */}
      <div className="flex-1 relative overflow-hidden bg-gray-50" ref={graphContainerRef}>
        {exploreMutation.isPending ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-acad-500 animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Building connections graph...</p>
              <p className="text-xs text-gray-400 mt-1">Analysing relationships between resources</p>
            </div>
          </div>
        ) : hasGraph ? (
          <>
            {/* Graph canvas */}
            <ConnectionsGraph
              nodes={graphNodes}
              edges={graphEdges}
              onNodeClick={handleNodeClick}
              onExpandNode={handleExpandNode}
              expandedNodeIds={expandedNodes}
              isExpanding={discoverMutation.isPending}
              layout={layout}
              selectedNodeId={selectedNode?.id}
              className="w-full h-full"
            />

            {/* Controls overlay */}
            <ConnectionsControls
              layout={layout}
              onLayoutChange={setLayout}
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onFullscreen={handleFullscreen}
              isFullscreen={isFullscreen}
              onDiscover={handleDiscover}
              isDiscovering={discoverMutation.isPending}
              nodeCount={graphNodes.length}
              edgeCount={graphEdges.length}
              hasSelectedNode={!!selectedNode}
            />

            {/* Legend overlay */}
            <ConnectionsLegend />

            {/* Node detail panel */}
            <NodeDetailPanel
              node={selectedNode}
              edges={graphEdges}
              allNodes={graphNodes}
              onClose={() => setSelectedNode(null)}
              onOpenResource={handleOpenResource}
              onFindRelated={handleFindRelated}
              onDiscoverMore={handleDiscoverFromNode}
              isDiscovering={discoverMutation.isPending}
            />

            {/* Discovery progress */}
            <DiscoveryProgress
              isVisible={discoverMutation.isPending}
              discoveredCount={discoveredCount}
              onCancel={handleCancelDiscovery}
            />
          </>
        ) : (
          /* Empty state */
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center max-w-md px-6">
              <Network className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h2 className="text-lg font-semibold text-gray-700 mb-2">
                Explore Resource Connections
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                Search for a journal article, video, course, or other resource above to
                visualise its connections. The graph reveals semantic similarities,
                citations, shared topics, and AI-discovered cross-domain relationships.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {['quantum computing', 'climate adaptation', 'neural networks'].map(term => (
                  <button
                    key={term}
                    onClick={() => {
                      setSearchQuery(term)
                      searchMutation.mutate(term)
                    }}
                    className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full
                               text-gray-600 hover:border-acad-300 hover:text-acad-600
                               transition-colors"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Error display */}
        {(exploreMutation.isError || discoverMutation.isError) && (
          <div className="absolute top-4 right-4 z-30 bg-red-50 border border-red-200 rounded-lg
                          px-4 py-3 max-w-sm shadow-sm">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Connection Error</p>
                <p className="text-xs text-red-600 mt-0.5">
                  {exploreMutation.error?.message || discoverMutation.error?.message || 'Failed to load connections. Please try again.'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Helper: get colour for a resource type
function getTypeColor(type: string): string {
  const colors: Record<string, string> = {
    journal: '#8B5CF6',
    video: '#EF4444',
    audio: '#3B82F6',
    image: '#22C55E',
    website: '#14B8A6',
    course: '#F59E0B',
    document: '#6B7280',
  }
  return colors[type] || '#6B7280'
}
